import React, { memo } from 'react';
import { MechanicalContractor } from './types';

interface MechanicalContractorLocalTradesProps {
  contractorTrades?: MechanicalContractor['contractorTrades'];
}

const MechanicalContractorLocalTrades = memo(({
  contractorTrades
}: MechanicalContractorLocalTradesProps) => {
  // Get unique local unions with their trades
  const localUnions = (contractorTrades || []).reduce((acc, contractorTrade) => {
    if (contractorTrade.localUnion) {
      const localKey = contractorTrade.localUnion.id;
      if (!acc[localKey]) {
        acc[localKey] = {
          id: contractorTrade.localUnion.id,
          code: contractorTrade.localUnion.code,
          unionNumber: contractorTrade.localUnion.unionNumber,
          trades: []
        };
      }
      // Add trade if it exists and isn't already in the list
      if (contractorTrade.trade && !acc[localKey].trades.some(t => t.id === contractorTrade.trade?.id)) {
        acc[localKey].trades.push({
          id: contractorTrade.trade.id,
          name: contractorTrade.trade.name
        });
      }
    }
    return acc;
  }, {} as Record<string, {
    id: string;
    code: string;
    unionNumber: number;
    trades: { id: string; name: string }[]
  }>);

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">Local Unions</h2>
      </div>

      {/* Table */}
      <div className="p-6">
        {Object.keys(localUnions).length === 0 ? (
          <p className="text-gray-500 italic">No local unions assigned</p>
        ) : (
          <div className="space-y-4">
            {/* Table Headers */}
            <div className="grid grid-cols-2 gap-8 pb-4 border-b border-gray-200">
              <div className="text-sm font-medium text-gray-900">Locals</div>
              <div className="text-sm font-medium text-gray-900">Trades</div>
            </div>

            {/* Table Rows */}
            {Object.values(localUnions).map((local) => (
              <div key={local.id} className="grid grid-cols-2 gap-8 py-3 border-b border-gray-100">
                <div className="text-sm text-gray-900">
                  Local {local.unionNumber}
                </div>
                <div className="text-sm text-gray-700">
                  {local.trades.length > 0 ? (
                    <ul className="list-disc list-inside space-y-1">
                      {local.trades.map((trade) => (
                        <li
                          key={trade.id}
                          className="text-sm text-gray-900"
                        >
                          {trade.name}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-gray-500 italic">No trades assigned</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

MechanicalContractorLocalTrades.displayName = 'MechanicalContractorLocalTrades';

export default MechanicalContractorLocalTrades;